/**
 * HR attendance register (prototype: /wfh/register, HR+ only).
 *
 * Every WFH and out-on-business record, all teams: outstanding (unreviewed)
 * section, period/type/status filters, the prototype's four stat cards, and
 * the record table. Read-only — the register is HR's audit surface, decisions
 * happen in the approvals inbox.
 */
import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { getWfhRegister, type WfhRegisterRow } from '../api/wfh'
import { Badge, type BadgeVariant } from '../components/primitives/Badge'
import { Button } from '../components/primitives/Button'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { ThinkingDots } from '../components/primitives/ThinkingDots'
import { useSession } from '../auth/auth-context'

const STATUS_BADGE: Record<string, { variant: BadgeVariant; label: string }> = {
  pending: { variant: 'warning', label: 'Pending' },
  approved: { variant: 'success', label: 'Approved' },
  rejected: { variant: 'danger', label: 'Rejected' },
  cancelled: { variant: 'default', label: 'Cancelled' },
  logged: { variant: 'info', label: 'Logged' },
}

const FLAG_LABEL: Record<string, string> = {
  same_day: 'Same-day',
  retrospective: 'Retrospective',
  logged_late: 'Logged late',
}

const tableStyle: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }
const thStyle: CSSProperties = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-2)',
  borderBottom: '1px solid var(--border)',
  whiteSpace: 'nowrap' as const,
}
const tdStyle: CSSProperties = {
  padding: '10px 12px',
  borderBottom: '1px solid var(--border)',
  color: 'var(--text-2)',
  verticalAlign: 'top',
}
const selectStyle: CSSProperties = {
  minHeight: 38,
  padding: '6px 10px',
  border: '1px solid var(--border-strong)',
  borderRadius: 8,
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'inherit',
  fontSize: 13,
}

function fmt(s: string): string {
  return format(new Date(s), 'EEE d MMM yyyy')
}

function rangeLabel(r: WfhRegisterRow): string {
  return r.startDate === r.endDate ? fmt(r.startDate) : `${fmt(r.startDate)} → ${fmt(r.endDate)}`
}

export function WfhRegisterPage(): JSX.Element {
  const { user } = useSession()

  // Period presets from the prototype; custom exposes from/to date inputs.
  const [period, setPeriod] = useState<'this-month' | 'last-month' | 'custom' | 'all'>(
    'this-month',
  )
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [type, setType] = useState<'all' | 'wfh' | 'business'>('all')
  const [status, setStatus] = useState('all')
  const [q, setQ] = useState('')

  const { from, to } = useMemo((): { from: string | undefined; to: string | undefined } => {
    const now = new Date()
    if (period === 'this-month') {
      return {
        from: format(new Date(now.getFullYear(), now.getMonth(), 1), 'yyyy-MM-dd'),
        to: format(new Date(now.getFullYear(), now.getMonth() + 1, 0), 'yyyy-MM-dd'),
      }
    }
    if (period === 'last-month') {
      const base = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      return {
        from: format(base, 'yyyy-MM-dd'),
        to: format(new Date(base.getFullYear(), base.getMonth() + 1, 0), 'yyyy-MM-dd'),
      }
    }
    if (period === 'custom') {
      return { from: customFrom || undefined, to: customTo || undefined }
    }
    return { from: undefined, to: undefined }
  }, [period, customFrom, customTo])

  const reg = useQuery({
    queryKey: ['wfh', 'register', from, to, type, status, q],
    queryFn: () =>
      getWfhRegister({
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(type === 'all' ? {} : { type }),
        status,
        q,
      }),
  })

  const rows = reg.data?.rows ?? []
  const stats = reg.data?.stats

  const periodLabel = useMemo((): string => {
    if (period === 'this-month') return format(new Date(), 'MMMM yyyy')
    if (period === 'last-month') {
      const base = new Date()
      return format(new Date(base.getFullYear(), base.getMonth() - 1, 1), 'MMMM yyyy')
    }
    if (period === 'custom' && customFrom) {
      const f = format(new Date(customFrom), 'd MMM')
      const t = customTo ? format(new Date(customTo), 'd MMM') : '…'
      return `${f}–${t}`
    }
    return 'all time'
  }, [period, customFrom, customTo])

  function exportCsv(): void {
    const header = ['Employee', 'Team', 'Type', 'Dates', 'Days', 'Flag', 'Status', 'Detail']
    const lines = rows.map((r) =>
      [
        r.employeeName,
        r.teamName ?? '',
        r.requestType === 'wfh' ? 'Work from home' : 'Out on business',
        rangeLabel(r),
        String(r.days),
        r.timingFlag ? FLAG_LABEL[r.timingFlag] : '',
        STATUS_BADGE[r.status]?.label ?? r.status,
        r.detail.replace(/"/g, "'"),
      ]
        .map((cell) => `"${cell}"`)
        .join(','),
    )
    const blob = new Blob([[header.join(','), ...lines].join('\n')], {
      type: 'text/csv;charset=utf-8',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `attendance-register-${period}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="page" data-testid="wfh-register-page">
      <PageHead
        eyebrow="HR"
        title="Attendance"
        subtitle="Every WFH and out-on-business record, all teams."
        actions={
          <Button variant="tertiary" size="sm" onClick={exportCsv} disabled={rows.length === 0}>
            Export {periodLabel}
          </Button>
        }
      />

      <div style={{ maxWidth: 1080, width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Outstanding — pending records whose date has passed with no decision. */}
        {reg.data && reg.data.outstanding.length > 0 && (
          <div
            role="note"
            style={{
              padding: '12px 16px',
              background: 'var(--warning-bg)',
              border: '1px solid var(--warning-line)',
              borderRadius: 'var(--r-md)',
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'var(--warning-fg)',
              }}
            >
              Outstanding · date passed with no decision
            </div>
            {reg.data.outstanding.map((o) => {
              const days = Math.max(
                0,
                Math.round((Date.now() - new Date(o.endDate).getTime()) / 86400000),
              )
              return (
                <div key={o.id} style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  <strong style={{ color: 'var(--text)' }}>{o.employeeName}</strong>{' '}
                  {o.requestType === 'wfh' ? 'Work from home' : 'Out on business'} ·{' '}
                  {rangeLabel(o)}{' '}
                  <span style={{ color: 'var(--warning-fg)' }}>
                    No decision for {days} day{days === 1 ? '' : 's'}
                  </span>
                </div>
              )
            })}
          </div>
        )}

        {/* Filters */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
            Period
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value as typeof period)}
              style={selectStyle}
            >
              <option value="this-month">This month</option>
              <option value="last-month">Last month</option>
              <option value="custom">Custom range</option>
              <option value="all">All time</option>
            </select>
          </label>
          {period === 'custom' && (
            <>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
                From
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  style={selectStyle}
                />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
                To
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  style={selectStyle}
                />
              </label>
            </>
          )}
          <div style={{ display: 'inline-flex', background: 'var(--surface-2)', borderRadius: 10, padding: 3, gap: 2 }}>
            {(['all', 'wfh', 'business'] as const).map((t) => (
              <button
                key={t}
                type="button"
                aria-pressed={type === t}
                onClick={() => setType(t)}
                style={{
                  minHeight: 34,
                  padding: '0 14px',
                  border: 'none',
                  borderRadius: 8,
                  cursor: 'pointer',
                  background: type === t ? 'var(--surface)' : 'transparent',
                  color: type === t ? 'var(--text)' : 'var(--text-2)',
                  fontWeight: 500,
                  fontFamily: 'inherit',
                  fontSize: 13,
                }}
              >
                {t === 'all' ? 'All' : t === 'wfh' ? 'WFH' : 'Out on business'}
              </button>
            ))}
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
            Status
            <select value={status} onChange={(e) => setStatus(e.target.value)} style={selectStyle}>
              <option value="all">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="logged">Logged</option>
              <option value="unreviewed">Unreviewed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12, fontWeight: 600, color: 'var(--text-2)' }}>
            Search
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Employee name"
              style={{ ...selectStyle, minWidth: 180 }}
            />
          </label>
        </div>

        {/* Stat cards — the prototype's four. */}
        {stats && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
            {[
              { label: 'WFH days approved', value: String(stats.wfhDaysApproved) },
              { label: 'Out on business days', value: String(stats.businessDaysLogged) },
              { label: 'Same-day or late WFH', value: stats.flaggedWfh },
              { label: 'Unreviewed in period', value: String(stats.unreviewedCount) },
            ].map((k) => (
              <Card key={k.label} padding={14}>
                <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>{k.label}</div>
                <div style={{ fontSize: 26, fontWeight: 600, color: 'var(--text)', marginTop: 2 }}>
                  {k.value}
                </div>
              </Card>
            ))}
          </div>
        )}

        <Card padding={0}>
          {reg.isLoading ? (
            <div style={{ padding: 24 }}>
              <ThinkingDots />
            </div>
          ) : rows.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13.5, color: 'var(--text-2)' }}>
              No records match these filters.
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Team</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Dates</th>
                    <th style={thStyle}>Days</th>
                    <th style={thStyle}>Flag</th>
                    <th style={thStyle}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: 'var(--text)' }}>
                        {r.employeeName}
                      </td>
                      <td style={tdStyle}>{r.teamName ?? '—'}</td>
                      <td style={tdStyle}>
                        {r.requestType === 'wfh' ? 'Work from home' : 'Out on business'}
                      </td>
                      <td style={tdStyle}>{rangeLabel(r)}</td>
                      <td style={tdStyle}>
                        {Number.isInteger(r.days) ? r.days : r.days.toFixed(1)}
                      </td>
                      <td style={{ ...tdStyle, color: 'var(--warning-fg)' }}>
                        {r.timingFlag ? FLAG_LABEL[r.timingFlag] : ''}
                      </td>
                      <td style={tdStyle}>
                        {r.unreviewed ? (
                          <Badge variant="warning" tooltip="Date passed with no decision">
                            Unreviewed
                          </Badge>
                        ) : (
                          <Badge variant={STATUS_BADGE[r.status]?.variant ?? 'default'}>
                            {STATUS_BADGE[r.status]?.label ?? r.status}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
          {rows.length} {rows.length === 1 ? 'record' : 'records'}
          {user != null ? '' : ''}
        </div>
      </div>
    </div>
  )
}
